import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus, focusQuietly } from '@theme/focus';
import { lastInteractionWasKeyboard } from '@theme/dialog';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #activePanelId = 'root';

  /** Scopes listeners this component attaches to *other* elements (the drawer it opens). */
  #externalListeners = new AbortController();

  /** True from a handoff tap until its drawer has been told to open (or the handoff was abandoned). */
  #handoffPending = false;

  /**
   * AP: generation token for open() and close(). Each call takes the next value and
   * its continuations (the open's frame and animation wait, the close's animation
   * wait) do nothing once a later call has moved it on. Without it a tap on the
   * hamburger during the 200 ms close reopened the menu and the close's reset then
   * landed on the reopened menu, snapping it shut. Disconnect moves it on too, so a
   * continuation cannot act on a drawer that was removed and reinserted mid-fade.
   */
  #transition = 0;

  /**
   * AP: whether this open has been keyboard-driven - opened from the keyboard, or any
   * key but Escape pressed while it is open (the same rule dialog.js applies to its
   * pointer-open suppression). Focus handed back to the menu button on close is only
   * allowed to ring for keyboard users; WebKit paints a ring on script-moved focus
   * after a tap, which is what the customer saw after a tap-close.
   */
  #keyboardDriven = false;

  connectedCallback() {
    super.connectedCallback();

    this.#externalListeners = new AbortController();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('keydown', this.#onKeyDown);
    this.refs.menuDrawer.addEventListener('click', this.#onMenuDrawerClick);
    this.#setActivePanel('root', { focus: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.refs.menuDrawer.removeEventListener('click', this.#onMenuDrawerClick);
    this.#externalListeners.abort();
    this.#transition += 1;

    // AP: symmetric teardown for a drawer removed while open (a header
    // re-render in the editor). The global focus trap would otherwise keep
    // redirecting every focusin on the document to a detached element, and
    // html[scroll-lock] would stay set: it is only cleared by the toggle
    // listener in dialog.js, which needs a toggle event that a detached
    // <details> never delivers to the document. Only a drawer that was open
    // holds a lock, so a closed drawer leaves the attribute alone - the lookbook
    // lightbox (ap-lookbook-gallery.js) sets the same attribute from a <dialog>
    // and must survive a header re-render. An open drawer releases it the way
    // the toggle listener would on close, unless another locking <details> is
    // still open.
    const wasOpen = this.isOpen;

    removeTrapFocus();
    reset(this.refs.details);

    if (wasOpen && !document.querySelector('details[scroll-lock][open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the main menu drawer
   */
  open() {
    const summary = this.refs.details.querySelector('summary');

    if (!summary) return;

    const transition = ++this.#transition;

    this.#keyboardDriven = lastInteractionWasKeyboard();
    summary.setAttribute('aria-expanded', 'true');
    this.#setActivePanel('root', { focus: false });

    requestAnimationFrame(() => {
      // AP: both continuations can outlive the element (re-render mid-open) or
      // the open itself (closed before the fade finished, or superseded by a
      // later open or close); never trap focus in a drawer that is detached or
      // already closed.
      if (transition !== this.#transition || !this.isConnected) return;

      this.refs.details.classList.add('menu-open');

      onAnimationEnd(this.refs.menuDrawer, () => {
        if (transition !== this.#transition || !this.isConnected || !this.isOpen) return;
        trapFocus(this.refs.menuDrawer);
      }, { subtree: false });
    });
  }

  /**
   * Return to the root panel, or close the drawer if already at root
   */
  back() {
    if (this.#activePanelId !== 'root') {
      this.#setActivePanel('root', { focus: true });
      return;
    }

    this.close();
  }

  /**
   * Close the main menu drawer.
   *
   * Returns a promise that settles once the drawer has been reset - after the fade-out,
   * or as soon as the <details> reports itself closed, whichever comes first - so a
   * caller can await a fully closed menu when it needs one. A close superseded by a
   * later open() or close() settles without resetting anything (the later call owns
   * the drawer's state); check `isOpen` afterwards when it matters.
   *
   * @returns {Promise<void>}
   */
  async close() {
    const { details, menuDrawer } = this.refs;
    const summary = details.querySelector('summary');

    if (!summary) return;

    const transition = ++this.#transition;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // AP: a summary tap closes the <details> natively as part of the same click, and
    // Chrome then skips rendering its content (::details-content is
    // content-visibility: hidden): the fade is never seen and the transition's
    // `finished` promise never settles, so awaiting the animation alone parked the
    // reset, the trap removal and the focus return until the next open cancelled
    // the transition - at which point the reset landed on the reopened menu. Finish
    // on the details' own toggle event instead when that comes first; the close
    // button, Escape and the menu-to-drawer handoff keep `open` until reset() and
    // still wait for the fade.
    const toggleListener = new AbortController();
    const closedNatively = new Promise((resolve) => {
      // Already closed (a second close() inside the hidden fade): no toggle is
      // coming and the fade will never settle, so finish now.
      if (!details.open) {
        resolve(undefined);
        return;
      }

      details.addEventListener(
        'toggle',
        () => {
          if (!details.open) resolve(undefined);
        },
        { signal: toggleListener.signal }
      );
    });

    await Promise.race([onAnimationEnd(menuDrawer, () => undefined, { subtree: false }), closedNatively]);
    toggleListener.abort();

    if (transition !== this.#transition) return;

    this.#setActivePanel('root', { focus: false });
    reset(details);
    removeTrapFocus();

    // AP: hand focus back to the menu button when the drawer had it. Escape
    // and the drawer's own close button otherwise leave focus on <body> (the
    // focused control is inside a now-closed <details>), so keyboard and
    // screen-reader users lose their place. A summary click already holds
    // focus and is left alone. The menu-to-drawer handoff also passes
    // through here: the dialog it opens takes focus next and its dialog:close
    // listener returns it to the summary, so this is harmless there.
    if (!this.isConnected) return;

    const active = document.activeElement;
    if (!active || active === document.body || menuDrawer.contains(active)) {
      if (this.#keyboardDriven) {
        summary.focus({ preventScroll: true });
      } else {
        focusQuietly(summary);
      }
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    event.preventDefault();
    this.close();
  };

  /**
   * AP: a key other than Escape while the menu is open means a keyboard user - the
   * focus return on close must ring for them (see #keyboardDriven).
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (isEscapeOrModifier(event)) return;

    this.#keyboardDriven = true;
  };

  /**
   * Handle AP panel navigation inside the drawer
   * @param {MouseEvent} event
   */
  #onMenuDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-menu-panel-trigger]');

    if (trigger instanceof HTMLElement && this.refs.menuDrawer.contains(trigger)) {
      event.preventDefault();
      const panelId = trigger.dataset.menuPanelTrigger;

      if (panelId) {
        this.#setActivePanel(panelId, { focus: true });
      }

      return;
    }

    const backButton = event.target.closest('[data-menu-panel-back]');

    if (backButton instanceof HTMLElement && this.refs.menuDrawer.contains(backButton)) {
      event.preventDefault();
      this.back();
      return;
    }

    const drawerTrigger = event.target.closest('[data-ap-drawer-trigger]');

    if (drawerTrigger instanceof HTMLElement && this.refs.menuDrawer.contains(drawerTrigger)) {
      event.preventDefault();
      this.#openDrawerFromMenu(drawerTrigger.dataset.apDrawerTrigger);
    }
  };

  /**
   * AP: open a drawer dialog from a menu item, closing the menu first.
   *
   * These triggers carry `data-ap-drawer-trigger` rather than the declarative
   * `on:click="#id/showDialog"` the other menus use, so that the menu is told to close
   * as part of the same click - a declarative trigger would open the drawer and leave
   * the full-screen menu sitting open underneath it.
   *
   * Focus is handed back to the menu button rather than the trigger, because by the time
   * the dialog closes the trigger is inside a closed <details> and can't take focus - the
   * browser's own restoration would drop the customer on <body>.
   *
   * @param {string | undefined} drawerId
   */
  #openDrawerFromMenu = async (drawerId) => {
    if (!drawerId || this.#handoffPending) return;

    const drawer = document.getElementById(drawerId);

    if (!drawer || typeof (/** @type {any} */ (drawer).showDialog) !== 'function') return;

    // The menu stays tappable while it fades, so a second trigger inside the 350ms
    // window would otherwise await the same close() and stack a second modal.
    this.#handoffPending = true;

    const summary = this.refs.details.querySelector('summary');
    const dialog = drawer.querySelector('dialog');

    // Match the drawer's slide-in to the menu's fade (header-drawer.liquid keys the
    // slower opening animation on this attribute). It stays on for the life of the
    // open so the guard selector can exclude the close animation, and is cleared on
    // dialog:close so a later open from anywhere else runs at stock speed.
    const { signal } = this.#externalListeners;
    const clearHandoffState = () => dialog?.removeAttribute('data-opened-from-menu');
    // The tap that started the handoff decides whether the eventual return to the
    // menu button may ring, unless the customer then uses the keyboard inside the
    // drawer (see #keyboardDriven - the same rule, applied to the drawer's lifetime).
    let keyboardDriven = lastInteractionWasKeyboard();
    const onDrawerKeyDown = (/** @type {KeyboardEvent} */ event) => {
      if (isEscapeOrModifier(event)) return;

      keyboardDriven = true;
    };
    const onDialogClose = () => {
      // A normal close is the common path; drop the abort fallback so repeated
      // handoffs don't accumulate closures on the component-lifetime signal.
      signal.removeEventListener('abort', clearHandoffState);
      drawer.removeEventListener('keydown', onDrawerKeyDown);
      clearHandoffState();

      if (!summary) return;

      if (keyboardDriven) {
        summary.focus();
      } else {
        focusQuietly(summary);
      }
    };

    dialog?.setAttribute('data-opened-from-menu', '');

    // Both live on the drawer, not on this component, so they are tied to this
    // component's lifetime explicitly: a header re-render while the drawer is open
    // would otherwise leave a listener holding a detached <summary>, and the
    // attribute stranded on the dialog (slowing its next open from anywhere).
    drawer.addEventListener('dialog:close', onDialogClose, { once: true, signal });
    drawer.addEventListener('keydown', onDrawerKeyDown, { signal });
    signal.addEventListener('abort', clearHandoffState, { once: true });

    // Sequenced, not overlapped: the drawer's ::backdrop is animation: none in
    // base.css, so opening while the menu is still fading drops a full-opacity
    // backdrop over it and cuts the fade off mid-way - it reads as the menu snapping
    // shut. Instead the menu gets a longer, eased fade for this handoff (the
    // attribute is what header-drawer.liquid keys the duration on) and the drawer
    // slides in only once that fade has finished and the menu has reset.
    this.refs.details.setAttribute('data-closing-to-drawer', '');

    try {
      await this.close();
    } finally {
      this.refs.details.removeAttribute('data-closing-to-drawer');
      this.#handoffPending = false;
    }

    // The await is the one place a stale continuation can outlive the component
    // (re-rendered mid-fade); its abort already cleared the handoff state above.
    if (signal.aborted || !this.isConnected) return;

    // The menu is open again: a hamburger tap during the fade superseded this
    // close (see close()). Opening the drawer now would stack it over the open
    // menu, which is exactly what this handoff exists to avoid, so abandon it and
    // take the drawer-side hooks back with it.
    if (this.isOpen) {
      drawer.removeEventListener('dialog:close', onDialogClose);
      drawer.removeEventListener('keydown', onDrawerKeyDown);
      signal.removeEventListener('abort', clearHandoffState);
      clearHandoffState();
      return;
    }

    /** @type {any} */ (drawer).showDialog();
  };

  /**
   * @param {string} panelId
   * @param {{ focus?: boolean }} options
   */
  #setActivePanel(panelId, { focus = true } = {}) {
    const panels = this.#getPanels();
    const targetPanel = panels.find((panel) => panel.dataset.menuPanel === panelId);

    if (!targetPanel) return;

    this.#activePanelId = panelId;

    panels.forEach((panel) => {
      const currentPanelId = panel.dataset.menuPanel;
      const isActive = currentPanelId === panelId;

      panel.classList.toggle('is-active', isActive);
      panel.classList.toggle('is-parent', panelId !== 'root' && currentPanelId === 'root');
      panel.classList.toggle('is-next', !isActive && !(panelId !== 'root' && currentPanelId === 'root'));
      panel.setAttribute('aria-hidden', String(!isActive));

      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });

    this.#getPanelTriggers().forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.menuPanelTrigger === panelId));
    });

    if (focus && this.isOpen) {
      requestAnimationFrame(() => {
        if (!this.isConnected || !this.isOpen) return;
        trapFocus(this.refs.menuDrawer);
      });
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getPanels() {
    return Array.from(this.refs.menuDrawer.querySelectorAll('[data-menu-panel]')).filter(
      (panel) => panel instanceof HTMLElement
    );
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getPanelTriggers() {
    return Array.from(this.refs.menuDrawer.querySelectorAll('[data-menu-panel-trigger]')).filter(
      (trigger) => trigger instanceof HTMLButtonElement
    );
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * AP: keys that do not count as keyboard navigation for the focus-return rule - Escape
 * closes, and modifier-only presses are not navigation (mirrors dialog.js).
 *
 * @param {KeyboardEvent} event
 * @returns {boolean}
 */
function isEscapeOrModifier(event) {
  return event.key === 'Escape' || event.metaKey || event.altKey || event.ctrlKey;
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
